import { theme } from 'antd';

import { generateDarkMapTokens } from '@common/utils/generate-dark-palette-colors';

import type { CustomDesignToken } from '../types/antd-theme-token';
import type { ThemeConfig } from 'antd';
import type { SeedToken, MapToken, AliasToken } from 'antd/es/theme/interface';

const darkModeDesignTokenOverrides: Partial<AliasToken> = {
  colorBgBase: '#121214',
  colorBgLayout: '#1e1e24',
  colorTextBase: '#fbfbfc',

  colorInfo: '#4282DB',
  colorPrimary: '#4282DB',
  colorSuccess: '#42DB7A',
  colorWarning: '#DB9642',
  colorError: '#DB4270',

  colorLink: '#6eabfa',
  colorLinkHover: '#90c2fc',
  colorLinkActive: '#3072db',
};

const lightModeDesignTokenOverrides: Partial<AliasToken> = {
  colorBgBase: '#ffffff',
  colorTextBase: '#1a1c21',

  colorInfo: '#1f7cff',
  colorPrimary: '#1f7cff',
  colorSuccess: '#1cb854',
  colorWarning: '#ffa02e',
  colorError: '#f9316d',
};

const customDarkModeDesignToken: CustomDesignToken = {
  colorFillAlterSolid: '#2d2d38',
  colorMethodDelete: '#fa82a2',
  colorMethodGet: '#68cc97',
  colorMethodPost: '#90b0f0',
  colorMethodPut: '#e0a663',
  colorMethodPatch: '#e0c363',
  colorMethodDefault: '#b3a6f9',
  colorTail: 'rgba(253, 253, 253, 0.12)',
};

const customLightModeDesignToken: CustomDesignToken = {
  colorFillAlterSolid: '#fafafa',
  colorMethodDelete: '#e70b46',
  colorMethodGet: '#25b869',
  colorMethodPost: '#1e65f5',
  colorMethodPut: '#f5901d',
  colorMethodPatch: '#f5c31d',
  colorMethodDefault: '#7a69ee',
  colorTail: 'rgba(5, 5, 5, 0.06)',
};

function withCustomDesignToken(derivative: (token: SeedToken) => MapToken) {
  return (token: SeedToken) => ({
    ...derivative(token),
    ...(derivative === theme.darkAlgorithm
      ? customDarkModeDesignToken
      : customLightModeDesignToken),
  });
}

// https://github.com/ant-design/ant-design/issues/44006#issuecomment-2773461322
function withoutSeedTokenOverrides(derivative: (token: SeedToken) => MapToken) {
  return (token: SeedToken) => {
    const mapToken = derivative(token);

    mapToken.colorPrimary = token.colorPrimary;
    mapToken.colorSuccess = token.colorSuccess;
    mapToken.colorWarning = token.colorWarning;
    mapToken.colorError = token.colorError;
    mapToken.colorInfo = token.colorInfo;
    mapToken.colorLink = token.colorLink;

    return mapToken;
  };
}

function withGeneratedDarkTokens(derivative: (token: SeedToken) => MapToken) {
  return (token: SeedToken) => {
    const mapToken = derivative(token);

    return {
      ...mapToken,
      ...generateDarkMapTokens(mapToken),
    };
  };
}

const customDarkModeAlgorithm = withGeneratedDarkTokens(
  withoutSeedTokenOverrides(withCustomDesignToken(theme.darkAlgorithm)),
);

const customLightModeAlgorithm = withoutSeedTokenOverrides(
  withCustomDesignToken(theme.defaultAlgorithm),
);

export const darkModeThemeConfig: ThemeConfig = {
  algorithm: customDarkModeAlgorithm,
  token: darkModeDesignTokenOverrides,
};

export const lightModeThemeConfig: ThemeConfig = {
  algorithm: customLightModeAlgorithm,
  token: lightModeDesignTokenOverrides,
};
